import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../hooks/apiClient";
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import { useStore } from "../../context/StoreContext";

function Row({ row, onRowClick }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: row.isReturn ? "#fff0f0" : "transparent",
          borderLeft: row.isReturn ? "3px solid #ff6b6b" : "none",
          height: 38,
          cursor: "pointer",
          "&:hover": { backgroundColor: row.isReturn ? "#ffe0e0" : "#f5f5f5" },
        }}
        onClick={() => onRowClick(row.id)}
      >
        <TableCell
          padding="checkbox"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
          }}
        >
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.id}</TableCell>
        <TableCell>{row.total_qty}</TableCell>
        <TableCell>{Number(row.total_price).toLocaleString()} PKR</TableCell>
        <TableCell>
          <Chip
            label={row.isDispatched ? "Dispatched" : row.status}
            size="small"
            color={row.isDispatched ? "success" : "default"}
          />
        </TableCell>
        <TableCell>{new Date(row.createdAt).toLocaleDateString()}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                    <TableCell>Product</TableCell>
                    <TableCell>Variant</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Barcode</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Price (PKR)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.dg_order_details?.map((detail) => (
                    <TableRow key={detail.id}>
                      <TableCell>{detail.product_title}</TableCell>
                      <TableCell>{detail.variant_title}</TableCell>
                      <TableCell>{detail.sku}</TableCell>
                      <TableCell>{detail.barcode}</TableCell>
                      <TableCell align="right">{detail.quantity}</TableCell>
                      <TableCell align="right">
                        {Number(detail.price).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const DGOrdersTable = () => {
  const { selectedStore, isStoreSelected } = useStore();
  const store_id = isStoreSelected && selectedStore.id;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!store_id) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`orders/dg/bystore/${store_id}`);
        setData(response.data);
      } catch (error) {
        console.error("Error fetching DG orders:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [store_id]);

  if (!isStoreSelected) {
    return (
      <Box p={3} display="flex" alignItems="center" gap={1}>
        <SyncProblemIcon color="warning" />
        <Typography>No Store Selected</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box p={3} display="flex" alignItems="center" gap={1}>
        <SyncProblemIcon />
        <Typography>No DG Orders Found</Typography>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Typography variant="h6" mb={2}>
        DG Orders
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
              <TableCell />
              <TableCell>Order #</TableCell>
              <TableCell>Total Qty</TableCell>
              <TableCell>Total Price</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <Row
                key={row.id}
                row={row}
                onRowClick={(id) => navigate(`/dg/orders/${id}`)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DGOrdersTable;
